import { useState, useEffect, useCallback, useContext } from "react";
import ApiService from "../../services/ApiService";
import { IAlert, IAssignee, IDoctor, IReportType, ITextInput, IUserReport } from "../../helpers/Interfaces";
import Show from "../../components/Show/Show";
import { UserReportFilterTypes } from "../../helpers/Types";
import InputDropDown from "../../components/InputDropDown/InputDropDown";
import DateInput from "../../components/DateInput/DateInput";
import DocumentButton from "../../components/DocumentButton/DocumentButton";
import moment from "moment";
import DownloadOrange from "../../assets/images/DownloadOrange.svg";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useNavigate } from "react-router-dom";
import { convertDuration, isInRole } from "../../helpers/HelperFunctions";
import { User } from "../../store/Store";
import UsersUI from "./UsersUI";
import ViewUserReport from "../../components/ViewUserReport/ViewUserReport";
import MuiAlert from "../../components/Alert/Alert";

import "./Users.scss";

export default function Users() {

    const apiService = new ApiService();

    const [reportModals, setReportModals] = useState({ view: false, edit: false });
    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState([] as IUserReport[]);
    const [filteredReports, setFilteredReports] = useState([] as IUserReport[]);
    const [selectedReport, setSelectedReport] = useState({} as IUserReport);
    const [showAlert, setShowAlert] = useState({} as IAlert);
    const [permission, setPermission] = useState([] as string[]);
    const [formData, setFormData] = useState({
        userId: null, doctorId: null, reportTypeId: null, fromDate: moment().utc().subtract(30, 'days').local(), toDate: moment().utc().local(),
    });
    const [formDataErrors, setFormDataErrors] = useState({
        userError: { showError: false, message: "" },
    });
    const [users, setUsers] = useState([] as ITextInput[]);
    const [doctors, setDoctors] = useState([] as ITextInput[]);
    const [reportTypes, setReportTypes] = useState([] as ITextInput[]);
    const [selectedDateFilter, setSelectedDateFilter] = useState<"createdDate" | "dueDate" | "submittedDate" | "completedDate">("dueDate");
    const [filterState, setFilterState] = useState<"ASCENDING" | "DESCENDING" | null>(null);

    const [user] = useContext(User);

    const navigate = useNavigate();

    useEffect(() => {
        if (!isInRole(user?.roles, "Admin")) {
            return navigate("/dashboard/reports");
        }
    }, [user])

    useEffect(() => {
        getPermissions();
        getDoctors();
        getUsers();
    }, [])

    useEffect(() => {
        if (formData.doctorId) {
            getReportTypes();
        }
    }, [formData.doctorId])

    useEffect(() => {
        setFilteredReports(sortReports(reports));
    }, [reports])

    useEffect(() => {
        if (filterState === null) {
            setFilteredReports(sortReports(reports));
        }
    }, [filterState])

    function resetDefault() {
        setFilteredReports(sortReports(reports));
    }

    function filter(filterBy: UserReportFilterTypes, query: string) {
        let filteredList = filteredReports.filter((report) => {
            return report[filterBy] === query;
        })

        setFilteredReports(filteredList);
    }

    function getDoctors() {

        setLoading(true);

        apiService.getDoctors().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getDoctorTextInput(data?.doctors);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getReportTypes() {

        setLoading(true);

        let data = {
            doctorId: formData.doctorId
        }

        apiService.getReportTypesByDoctorIdOrClinicId(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getReportTypeTextInput(data?.reportTypes);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getUsers() {

        setLoading(true);

        apiService.getAssignees().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getUsersTextInput(data?.users);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getUsersTextInput(assignees: IAssignee[]) {

        setLoading(true);

        const userArr = assignees
            .filter((user, index, self) => index === self.findIndex((u) => u.email === user.email))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(assignee => ({ label: assignee.name, value: assignee.email }));

        setUsers(userArr);
        setLoading(false);
    }

    function getDoctorTextInput(doctors: IDoctor[]) {

        setLoading(true);

        let doctorsArr = [] as ITextInput[];

        doctors.forEach(doctor => {
            let data = {
                label: doctor.firstName + " " + doctor.lastName,
                value: doctor.id
            }

            doctorsArr.push(data);
        });

        setDoctors(doctorsArr);

        setLoading(false);
    }

    function getReportTypeTextInput(reportTypes: IReportType[]) {

        setLoading(true);

        let reportTypesArr = [] as ITextInput[];

        reportTypes.forEach(reportType => {
            let data = {
                label: `${reportType.reportName} - ${reportType.clinicName}`,
                value: reportType.id
            }

            reportTypesArr.push(data);
        });

        setReportTypes(reportTypesArr);

        setLoading(false);
    }

    function getPermissions() {

        setLoading(true);

        let data = {
            page: "Reports",
        };

        apiService.getPermission(data).then((result: any) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            setPermission(data?.actions);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
        }).finally(() => {
            setLoading(false);
        });
    }

    const handleOpen = (modal: "view" | "edit") => {
        let newReportModals = reportModals;
        newReportModals[modal] = true;
        setReportModals((prevDetails) => ({ ...prevDetails, ...newReportModals }));
    }

    const handleClose = (modal: "view" | "edit", shouldUpdate = false) => {
        let newReportModals = reportModals;
        newReportModals[modal] = false;
        setReportModals((prevDetails) => ({ ...prevDetails, ...newReportModals }));

        if (shouldUpdate) {
            searchReports();
        }
    }

    const sortReports = (reports: IUserReport[]): IUserReport[] => {
        const priorityOrder: { [key: string]: number } = {
            "S. Urgent": 1,
            "Urgent": 2,
            "24 Hours": 3,
            "48 Hours": 4,
            "72 Hours": 5
        };

        return reports.sort((a, b) => {
            const priorityA = priorityOrder[a.priority ?? ""] ?? Infinity;
            const priorityB = priorityOrder[b.priority ?? ""] ?? Infinity;

            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            const rawDateA = a.dueDate;
            const rawDateB = b.dueDate;

            const dateA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
            const dateB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

            return dateA - dateB;
        });
    };

    const sortReportsBySelectedDateFilter = (reports: IUserReport[]): IUserReport[] => {
        return reports.sort((a, b) => {
            const rawDateA = a[selectedDateFilter];
            const rawDateB = b[selectedDateFilter];

            const dateA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
            const dateB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

            return filterState === "ASCENDING" ? dateA - dateB : dateB - dateA;
        });
    };

    const searchReports = useCallback(() => {
        setLoading(true);

        if (formData.userId === null) {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.userError.showError = true;
            setFormDataErrors((prevDetails) => ({ ...prevDetails, newFormDataErrors }));
            setLoading(false);
            return;
        }

        let data = {
            userId: formData.userId,
            fromDate: moment(formData.fromDate).clone().toISOString(),
            toDate: moment(formData.toDate).clone().toISOString(),
            doctorId: formData.doctorId,
            reportTypeId: formData.reportTypeId,
        }

        apiService.getUserBreakdown(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, newShowAlert }));
                return;
            }

            if (data?.documents?.length === 0) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = "No Reports";
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
            }

            setReports(data?.documents);
        }).catch(() => {
        }).finally(() => {
            setLoading(false);
        });
    }, [formData])

    async function downloadReport() {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        worksheet.columns = [
            { header: 'Patient Name', key: 'patientName', width: 15 },
            { header: 'Clinic', key: 'clinic', width: 15 },
            { header: 'Type', key: 'reportType', width: 15 },
            { header: 'Total Minutes', key: 'totalDuration', width: 15 },
            { header: 'Stage', key: 'stage', width: 15 },
            { header: 'Status', key: 'documentStatus', width: 15 },
            { header: 'Submitted Date', key: 'submittedDate', width: 25 },
            { header: 'Doctor', key: 'doctorName', width: 25 },
            { header: 'Place of Assessment', key: 'placeOfAssessment', width: 25 },
            { header: 'Referrer', key: 'referrer', width: 25 },
            { header: 'Priority/TAT', key: 'priority', width: 15 },
        ];

        let downloadReports = [...reports];

        downloadReports = downloadReports.sort((a, b) => {
            const rawDateA = a.submittedDate ?? a.dueDate;
            const rawDateB = b.submittedDate ?? b.dueDate;

            const dateA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
            const dateB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

            return dateA - dateB;
        });

        const processedData = downloadReports.map(item => ({
            ...item,
            submittedDate: validDateField(item.submittedDate),
            totalDuration: convertDuration(item.totalDuration)
        }));

        worksheet.addRows(processedData);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${validDateField(new Date())}.xlsx`);
    }

    function validDateField(value: Date | undefined) {
        if (value) {
            return moment.utc(value).local().format("DD/MMM/YYYY");
        } else {
            return " - "
        }
    }

    function handleDateClick(selectedDateFilter: "createdDate" | "dueDate" | "submittedDate" | "completedDate") {
        if (filterState === "ASCENDING") {
            setFilterState("DESCENDING");
            setFilteredReports(sortReportsBySelectedDateFilter(reports));
        } else if (filterState === "DESCENDING") {
            setFilterState(null);
        } else {
            setFilterState("ASCENDING");
            setFilteredReports(sortReportsBySelectedDateFilter(reports));
        }

        setSelectedDateFilter(selectedDateFilter);
    }

    function getTotalMinutes(stage: 'scribe' | 'proof' | 'review') {
        let count = filteredReports.reduce((sum, report) => {
            const hasStageDetails = !!report.stageDetails?.[stage];
            return sum + (hasStageDetails ? (report.totalDuration ?? 0) : 0);
        }, 0);

        return convertDuration(count);
    }

    return (<div className="search-reports-container">
        <MuiAlert error={showAlert.isError} message={showAlert.message} open={showAlert.open}
            onClose={() => {
                setShowAlert((prevDetails) => ({ ...prevDetails, open: false }));
            }} />
        <div className="search-filter-wrapper">
            <InputDropDown title='USERS*' type='dropdown'
                defaultValue={formData.userId} values={users} showError={formDataErrors.userError.showError}
                onSelect={(value: any) => {
                    if (value?.length > 0) {
                        setFormData((prev) => ({ ...prev, userId: value[0].value }));
                    }
                }} />
            <InputDropDown title='DOCTOR' type='dropdown'
                defaultValue={formData.doctorId} values={doctors}
                onSelect={(value: any) => {
                    if (value?.length > 0) {
                        setFormData((prev) => ({ ...prev, doctorId: value[0].value }));
                    }
                }} />
            <InputDropDown title='REPORT TYPE' type='dropdown'
                defaultValue={formData.reportTypeId} values={reportTypes}
                onSelect={(value: any) => {
                    if (value?.length > 0) {
                        setFormData((prev) => ({ ...prev, reportTypeId: value[0].value }));
                    }
                }} />
            <DateInput label="FROM*" value={formData.fromDate.toISOString()}
                showError={false}
                onChange={(value: any) => {
                    if (value) {
                        setFormData((prev) => ({ ...prev, fromDate: value }));
                    }
                }} />
            <DateInput label="TO*" value={formData.toDate.toISOString()}
                showError={false}
                onChange={(value: any) => {
                    if (value) {
                        setFormData((prev) => ({ ...prev, toDate: value }));
                    }
                }} />
            <DocumentButton loading={loading} onClick={() => { searchReports(); }} text={"Search"} />
            <img className="download-content" src={DownloadOrange} alt="Download" onClick={() => { downloadReport(); }} />
        </div>
        <div className="stats-wrapper">
            <span>Total Reports: {filteredReports.length}</span>
            <span>Total Minutes Scribed: {getTotalMinutes("scribe")}</span>
            <span>Total Minutes Proofed: {getTotalMinutes("proof")}</span>
            <span>Total Minutes Reviewed: {getTotalMinutes("review")}</span>
        </div>
        <Show hasAccess={permission?.includes("Read")}>
            <div className="table-wrapper">
                <UsersUI reports={filteredReports} setSelectedReport={setSelectedReport} openView={handleOpen}
                    filter={filter} showFilter={filteredReports.length !== reports.length} cancelFilter={resetDefault}
                    searchReports={searchReports} handleDateClick={handleDateClick} filterState={filterState}
                    selectedDateFilter={selectedDateFilter} />
            </div>
        </Show>

        {reportModals.view
            ? (<ViewUserReport open={reportModals.view} closeModal={handleClose} report={selectedReport} />)
            : (<></>)
        }
    </div>)
}